import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type { Distributor, ApiResponse } from '@/types';

const dynamicDistributorBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem('token');
      }
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const distributorApi = createApi({
  reducerPath: 'distributorApi',
  baseQuery: dynamicDistributorBaseQuery,
  tagTypes: ['Distributor'],
  endpoints: (builder) => ({
    getDistributors: builder.query<ApiResponse<Distributor[]>, { storeId?: string; includeInactive?: boolean; includeDeleted?: boolean } | void>({
      query: (params) => ({
        url: '/distributors',
        params: params || undefined,
      }),
      providesTags: ['Distributor'],
    }),

    getDistributor: builder.query<ApiResponse<Distributor>, string>({
      query: (id) => `/distributors/${id}`,
      providesTags: (_, __, id) => [{ type: 'Distributor', id }],
    }),

    createDistributor: builder.mutation<
      ApiResponse<Distributor>,
      {
        storeId: string;
        name: string;
        contactName?: string;
        phone?: string;
        email?: string;
        address?: string;
        description?: string;
        isActive?: boolean;
      }
    >({
      query: (data) => ({
        url: '/distributors',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Distributor'],
    }),

    updateDistributor: builder.mutation<
      ApiResponse<Distributor>,
      {
        id: string;
        data: {
          name?: string;
          contactName?: string;
          phone?: string;
          email?: string;
          address?: string;
          description?: string;
          isActive?: boolean;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/distributors/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Distributor', id }, 'Distributor'],
    }),

    deleteDistributor: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/distributors/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Distributor'],
    }),
  }),
});

export const {
  useGetDistributorsQuery,
  useGetDistributorQuery,
  useCreateDistributorMutation,
  useUpdateDistributorMutation,
  useDeleteDistributorMutation,
} = distributorApi;
